import type { ContentUpdate } from "@greg-chick/shared";
import { adminApi } from "./contentApi";

/**
 * 📡 **게시 알림 받기(관리자)** — 누가 무엇을 올렸는지 실시간으로 듣는다.
 *
 * ## 왜 관리자에도 필요한가
 * 「✅ 출시 준비」는 **아이 화면 기준으로 남은 일**을 센다. 그런데 화면을 열어 둔 채로 다른
 * 창에서 굿즈 주소를 채워 올리면, 이 화면은 여전히 옛 숫자를 보여준다 — 다 채웠는데도
 * "6개 남음"이라고 적혀 있으면 그 목록을 못 믿게 된다.
 *
 * 아이 화면(`apps/web/src/lib/liveReload.ts`)이 쓰던 것과 **같은 스트림**이다.
 * 엣지가 게시 때마다 밀어 주고, 관리자 출처도 CORS 로 허용돼 있다.
 *
 * ## 없어도 망가지지 않는다
 * 푸시는 **있으면 좋은 것**이다. 엣지가 꺼져 있거나 연결이 계속 실패하면 조용히 단념한다
 * (무한 재연결로 배터리·요청을 태우지 않는다). 그때도 화면은 열 때마다 다시 세므로 멀쩡하다.
 */
export type { ContentUpdate };

type Handler = (u: ContentUpdate) => void;

let es: EventSource | null = null;
let errorStreak = 0;
let disabled = false;
const handlers = new Set<Handler>();
const MAX_ERROR_STREAK = 5;

function ensureConnection(): void {
  if (es || disabled || typeof EventSource === "undefined") return;
  try {
    es = new EventSource(`${adminApi()}/api/admin-events`);
    es.addEventListener("open", () => {
      errorStreak = 0;
    });
    es.addEventListener("update", (ev) => {
      try {
        const u = JSON.parse((ev as MessageEvent).data) as ContentUpdate;
        handlers.forEach((h) => h(u));
      } catch {
        /* 잘못된 프레임 무시 */
      }
    });
    es.onerror = () => {
      // EventSource 는 스스로 다시 붙는다(retry:3000). 연속 실패가 쌓이면 단념한다.
      if (++errorStreak >= MAX_ERROR_STREAK) {
        es?.close();
        es = null;
        disabled = true;
      }
    };
  } catch {
    es = null;
  }
}

/** 게시 알림 구독. 반환값을 부르면 해지(마지막 구독이 끊기면 연결도 닫는다). */
export function subscribeAdminUpdates(handler: Handler): () => void {
  handlers.add(handler);
  ensureConnection();
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0 && es) {
      es.close();
      es = null;
    }
  };
}
